import React, { useEffect, useState } from "react";
import { HiCheckCircle, HiOutlineXMark } from "react-icons/hi2";
import axiosInstance from "../../services/axiosInstance";
import styles from "./SmartUpgradeModal.module.css";
import {
  featuresGainedTo,
  planNameForHierarchy,
  type RequiredPlan,
} from "../PlanGate/featureCopy";
import {
  monthlyNetOf,
  usePlanOffer,
  type PlanLimits,
} from "../PlanGate/usePlanOffer";
import { formatMoney } from "../../modules/account/utilities/accountFormat";

interface SmartUpgradeModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** resourceType del 400 PLAN_LIMIT_REACHED: user | comanda_user | cashbox | warehouse | sede */
  extraType: string;
  /** Uso y cupo que trae el error (si faltan se consultan). */
  current?: number;
  limit?: number;
}

interface ResourceMeta {
  singular: string;
  plural: string;
  feminine: boolean;
  limitKey: keyof PlanLimits;
}

const RESOURCES: Record<string, ResourceMeta> = {
  user: { singular: "usuario", plural: "usuarios", feminine: false, limitKey: "users" },
  comanda_user: {
    singular: "usuario de comanda",
    plural: "usuarios de comanda",
    feminine: false,
    limitKey: "comandaUsers",
  },
  cashbox: { singular: "caja", plural: "cajas", feminine: true, limitKey: "cashboxes" },
  warehouse: { singular: "almacén", plural: "almacenes", feminine: false, limitKey: "warehouses" },
  sede: { singular: "sede", plural: "sedes", feminine: true, limitKey: "sites" },
};

/** En la BD "ilimitado" es 10000: no tiene sentido decir "incluye 10000". */
const UNLIMITED = 10000;
/** Misma regla que la recomendación inteligente del backend: si el adicional
 *  cuesta al menos el 70% de la diferencia al siguiente plan, conviene el plan. */
const UPGRADE_THRESHOLD = 0.7;

const nextPlanFor = (extraType: string, hierarchy: number): RequiredPlan | null => {
  if (!hierarchy) return null;
  if (extraType === "sede") return hierarchy < 4000 ? "Pro" : null;
  if (hierarchy < 3000) return "Estándar";
  if (hierarchy < 4000) return "Pro";
  return null;
};

const usageSentence = (meta: ResourceMeta, planName: string | null, limit: number) => {
  const plan = planName ? `Tu plan ${planName}` : "Tu plan";
  if (limit === 1) {
    return `${plan} incluye 1 ${meta.singular} y ya ${meta.feminine ? "la" : "lo"} estás usando.`;
  }
  return `${plan} incluye ${limit} ${meta.plural} y ya ${meta.feminine ? "las usas todas" : "los usas todos"}.`;
};

/**
 * Modal de "llegaste al límite" (spec 007, FR-011): explica qué pasó y ofrece
 * comprar el adicional suelto con su precio mensual neto (opción principal) o
 * pasar al plan superior. Precios del catálogo del servidor; la compra abre el
 * CheckoutDrawer sin salir de la pantalla. Para sedes en planes menores a Pro
 * la única salida es pasar a Pro.
 */
export const SmartUpgradeModal: React.FC<SmartUpgradeModalProps> = ({
  isOpen,
  onClose,
  extraType,
  current,
  limit,
}) => {
  const { offer, buyPlan, buyAddon } = usePlanOffer(isOpen);
  const meta = RESOURCES[extraType] ?? RESOURCES.user;
  const [usage, setUsage] = useState<{ current?: number; limit?: number }>({
    current,
    limit,
  });

  // El evento puede llegar sin uso/cupo (p. ej. una caja bloqueada tras bajar
  // de plan): se consulta el cupo efectivo del recurso.
  useEffect(() => {
    setUsage({ current, limit });
    if (!isOpen || extraType === "sede" || (limit != null && current != null)) return;
    let active = true;
    axiosInstance
      .get(`/api/v2/account/plan-limits-check?resourceType=${extraType}`)
      .then((res) => {
        const data = res.data?.data ?? res.data;
        if (active && data) setUsage({ current: data.current, limit: data.limit });
      })
      .catch(() => undefined);
    return () => {
      active = false;
    };
  }, [isOpen, extraType, current, limit]);

  if (!isOpen) return null;

  const planName = planNameForHierarchy(offer.hierarchy);
  const effectiveLimit = usage.limit ?? offer.limits[meta.limitKey];
  const nextPlan = nextPlanFor(extraType, offer.hierarchy);
  const nextPrice = nextPlan ? monthlyNetOf(offer, nextPlan) : null;
  const currentPrice = planName ? monthlyNetOf(offer, planName) : null;
  const addonPrice = extraType === "sede" ? null : offer.addonsNet?.[extraType] ?? null;
  const showAddon = addonPrice != null && addonPrice > 0;
  const showUpgrade = nextPlan != null;

  const recommendUpgrade =
    showUpgrade &&
    (!showAddon ||
      (nextPrice != null &&
        currentPrice != null &&
        addonPrice! >= UPGRADE_THRESHOLD * (nextPrice - currentPrice)));

  const gained = nextPlan ? featuresGainedTo(nextPlan).slice(0, 4) : [];

  const onBackdrop = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) onClose();
  };

  const handleAddon = () => {
    onClose();
    buyAddon(extraType);
  };
  const handleUpgrade = () => {
    if (!nextPlan) return;
    onClose();
    buyPlan(nextPlan);
  };

  const sedeWithoutPro = extraType === "sede" && nextPlan === "Pro";

  return (
    <div className={styles.backdrop} onClick={onBackdrop}>
      <div
        className={styles.modal}
        role="dialog"
        aria-modal="true"
        aria-labelledby="plan-limit-title"
        data-testid="plan-limit-modal"
      >
        <button type="button" className={styles.closeIcon} onClick={onClose} aria-label="Cerrar">
          <HiOutlineXMark size={20} />
        </button>

        <div className={styles.header}>
          <h2 id="plan-limit-title" className={styles.title}>
            {sedeWithoutPro
              ? "Abre más sedes con el plan Pro"
              : `Llegaste al límite de ${meta.plural} de tu plan`}
          </h2>
          <p className={styles.subtitle}>
            {sedeWithoutPro
              ? "Tu plan actual funciona con una sede. Para administrar varias sedes desde una sola cuenta necesitas el plan Pro."
              : effectiveLimit != null && effectiveLimit > 0 && effectiveLimit < UNLIMITED
                ? `${usageSentence(meta, planName, effectiveLimit)} Elige cómo seguir creciendo.`
                : `Ya usas ${meta.feminine ? "todas las" : "todos los"} ${meta.plural} que incluye tu plan. Elige cómo seguir creciendo.`}
          </p>
        </div>

        {offer.loading ? (
          <div className={styles.loader}>
            <div className={styles.spinner} />
          </div>
        ) : !showAddon && !showUpgrade ? (
          <p className={styles.emptyHint}>
            {extraType === "sede"
              ? "Crea la nueva sede desde Locales: se agrega a tu suscripción como sede adicional."
              : "Escríbenos a soporte y te ayudamos a ampliar tu capacidad."}
          </p>
        ) : (
          <div className={styles.optionsRow}>
            {showAddon && (
              <div
                className={`${styles.optionCard} ${!recommendUpgrade ? styles.recommended : ""}`}
                data-testid="plan-limit-addon"
              >
                {!recommendUpgrade && <span className={styles.badge}>Recomendado</span>}
                <h3 className={styles.optionTitle}>
                  Agregar 1 {meta.singular} adicional
                </h3>
                <p className={styles.optionPrice}>
                  {formatMoney(addonPrice)}{" "}
                  <span className={styles.optionPriceSuffix}>{offer.taxSuffix} / mes</span>
                </p>
                <p className={styles.optionDetail}>
                  Se suma a tu suscripción mensual, sin cambiar de plan.
                </p>
                {offer.canPurchase && (
                  <button
                    type="button"
                    className={`${styles.selectButton} ${
                      !recommendUpgrade ? styles.selectButtonPrimary : styles.selectButtonSecondary
                    }`}
                    onClick={handleAddon}
                  >
                    Agregar {meta.singular}
                  </button>
                )}
              </div>
            )}

            {showUpgrade && (
              <div
                className={`${styles.optionCard} ${recommendUpgrade ? styles.recommended : ""}`}
                data-testid="plan-limit-upgrade"
              >
                {recommendUpgrade && showAddon && (
                  <span className={styles.badge}>Recomendado</span>
                )}
                <h3 className={styles.optionTitle}>Pasar a {nextPlan}</h3>
                {nextPrice != null && (
                  <p className={styles.optionPrice}>
                    {formatMoney(nextPrice)}{" "}
                    <span className={styles.optionPriceSuffix}>{offer.taxSuffix} / mes</span>
                  </p>
                )}
                <p className={styles.optionDetail}>
                  {extraType === "sede"
                    ? "Varias sedes en una sola cuenta y además:"
                    : `Más ${meta.plural} incluidos y además:`}
                </p>
                {gained.length > 0 && (
                  <ul className={styles.featuresList}>
                    {gained.map((name) => (
                      <li key={name}>
                        <HiCheckCircle size={15} className={styles.featureIcon} aria-hidden />
                        {name}
                      </li>
                    ))}
                  </ul>
                )}
                {offer.canPurchase && (
                  <button
                    type="button"
                    className={`${styles.selectButton} ${
                      recommendUpgrade ? styles.selectButtonPrimary : styles.selectButtonSecondary
                    }`}
                    onClick={handleUpgrade}
                  >
                    Pasar a {nextPlan}
                  </button>
                )}
              </div>
            )}
          </div>
        )}

        {!offer.loading && !offer.canPurchase && (showAddon || showUpgrade) && (
          <p className={styles.adminHint} role="note">
            Pídele al administrador de tu negocio que
            {showAddon ? ` agregue más ${meta.plural}` : ""}
            {showAddon && showUpgrade ? " o" : ""}
            {showUpgrade ? ` active el plan ${nextPlan}` : ""}.
          </p>
        )}

        <div className={styles.closeRow}>
          <button type="button" className={styles.closeButton} onClick={onClose}>
            Ahora no
          </button>
        </div>
      </div>
    </div>
  );
};

export default SmartUpgradeModal;
